<template>
	<PCard>
		<template #title>
			<div class="flex align-items-center justify-content-between flex-wrap gap-3">
				<WTitle text="Manual Entries" />
				<div class="flex align-items-center gap-2 flex-wrap">
					<PDropdown
						:options="periodOptions"
						:showClear="true"
						optionLabel="label"
						optionValue="value"
						placeholder="All periods"
						style="min-width: 10rem;"
						v-model="filterPeriodId"
						@change="refreshList()"
					/>
					<PDropdown
						:options="statusOptions"
						:showClear="true"
						optionLabel="label"
						optionValue="value"
						placeholder="All statuses"
						style="min-width: 9rem;"
						v-model="filterStatus"
						@change="refreshList()"
					/>
					<PButton
						v-if="canCreate"
						@click="openSubmit()"
						icon="pi pi-plus"
						label="Submit"
						rounded
						size="small"
						v-tooltip.left="`Submit a manual entry`"
					/>
				</div>
			</div>
		</template>
		<template #content>
			<PDataTable
				:alwaysShowPaginator="false"
				:loading="loading"
				:rowHover="true"
				:rows="50"
				:showGridlines="true"
				:stripedRows="true"
				:value="list"
				data-key="id"
				paginator
				size="small"
			>
				<template #empty>No manual entries found.</template>

				<PColumn field="period_name" header="Period" headerClass="py-3" sortable />
				<PColumn field="variable_name" header="Variable" headerClass="py-3" sortable />
				<PColumn field="category" header="Category" headerClass="py-3" sortable />
				<PColumn field="value" header="Value" headerClass="py-3" sortable>
					<template #body="{ data }">
						<span>{{ displayValue(data) }}</span>
					</template>
				</PColumn>
				<PColumn field="status" header="Status" headerClass="py-3" style="width: 110px;">
					<template #body="{ data }">
						<PTag :severity="statusSeverity(data.status)">{{ data.status }}</PTag>
					</template>
				</PColumn>
				<PColumn field="submitted_by_username" header="Submitted by" headerClass="py-3" sortable />
				<PColumn field="submitted_at" header="Submitted at" headerClass="py-3">
					<template #body="{ data }">
						<span>{{ formatDate(data.submitted_at) }}</span>
					</template>
				</PColumn>
				<PColumn field="approved_by_username" header="Decided by" headerClass="py-3">
					<template #body="{ data }">
						<span>{{ data.approved_by_username || '—' }}</span>
					</template>
				</PColumn>
				<PColumn field="approved_at" header="Decided at" headerClass="py-3">
					<template #body="{ data }">
						<span>{{ formatDate(data.approved_at) }}</span>
					</template>
				</PColumn>
				<PColumn field="notes" header="Notes" headerClass="py-3">
					<template #body="{ data }">
						<span>{{ data.notes || '—' }}</span>
					</template>
				</PColumn>
				<PColumn
					v-if="canApprove"
					bodyClass="text-right"
					headerClass="py-3"
					style="width: 110px;"
				>
					<template #body="{ data }">
						<template v-if="data.status === 'pending'">
							<PButton
								:disabled="isOwnEntry(data)"
								@click="openApprove(data)"
								icon="pi pi-check"
								rounded
								severity="success"
								size="small"
								text
								v-tooltip.bottom="isOwnEntry(data)
									? 'Four-eyes: you cannot approve your own entry'
									: 'Approve'"
							/>
							<PButton
								:disabled="isOwnEntry(data)"
								@click="confirmReject($event, data)"
								icon="pi pi-times"
								rounded
								severity="danger"
								size="small"
								text
								v-tooltip.bottom="isOwnEntry(data)
									? 'Four-eyes: you cannot reject your own entry'
									: 'Reject'"
							/>
						</template>
					</template>
				</PColumn>
			</PDataTable>
		</template>
	</PCard>

	<!-- Submit dialog -->
	<PDialog
		header="Submit manual entry"
		@hide="closeSubmit()"
		modal
		style="width: 28rem; max-width: 95vw;"
		v-model:visible="showSubmit"
	>
		<div class="flex flex-column gap-4">
			<div class="flex flex-column gap-2">
				<label class="text-primary-50" for="me-period">Billing period</label>
				<PDropdown
					:options="eligiblePeriodOptions"
					id="me-period"
					optionLabel="label"
					optionValue="value"
					placeholder="Select period"
					v-model="form.billing_period_id"
				/>
			</div>
			<div class="flex flex-column gap-2">
				<label class="text-primary-50" for="me-var">Variable</label>
				<PDropdown
					:options="catalog"
					id="me-var"
					optionLabel="name"
					optionValue="id"
					placeholder="Select variable"
					v-model="form.variable_id"
				>
					<template #option="{ option }">
						<div class="flex flex-column">
							<span>{{ option.name }}</span>
							<small class="text-color-secondary">{{ option.category }} · {{ option.unit || '—' }}</small>
						</div>
					</template>
				</PDropdown>
			</div>
			<div class="flex flex-column gap-2">
				<label class="text-primary-50" for="me-value">Value</label>
				<PInputNumber
					:maxFractionDigits="5"
					id="me-value"
					inputClass="w-full"
					v-model="form.value"
				/>
			</div>
			<div class="flex flex-column gap-2">
				<label class="text-primary-50" for="me-text">Value text (optional)</label>
				<PInputText id="me-text" size="small" v-model="form.value_text" />
			</div>
			<div class="flex flex-column gap-2">
				<label class="text-primary-50" for="me-notes">Notes (optional)</label>
				<PInputText id="me-notes" size="small" v-model="form.notes" />
			</div>
			<div class="flex align-items-center justify-content-end gap-3">
				<PButton @click="doSubmit()" label="Submit" size="small" />
				<PButton
					@click="closeSubmit()"
					label="Cancel"
					outlined
					severity="secondary"
					size="small"
				/>
			</div>
		</div>
	</PDialog>

	<!-- Approve notes dialog -->
	<PDialog
		header="Approve manual entry"
		modal
		style="width: 24rem; max-width: 95vw;"
		v-model:visible="showApprove"
	>
		<div class="flex flex-column gap-4">
			<p class="m-0 text-sm">
				Approve
				<strong>{{ approveTarget?.variable_name }}</strong>
				for
				<strong>{{ approveTarget?.period_name }}</strong>
				and apply to settlement variables?
			</p>
			<div class="flex flex-column gap-2">
				<label class="text-primary-50" for="approve-notes">Notes (optional)</label>
				<PInputText id="approve-notes" size="small" v-model="decisionNotes" />
			</div>
			<div class="flex align-items-center justify-content-end gap-3">
				<PButton
					@click="doApprove()"
					label="Approve"
					severity="success"
					size="small"
				/>
				<PButton
					@click="showApprove = false"
					label="Cancel"
					outlined
					severity="secondary"
					size="small"
				/>
			</div>
		</div>
	</PDialog>

	<!-- Reject notes dialog -->
	<PDialog
		header="Reject manual entry"
		modal
		style="width: 24rem; max-width: 95vw;"
		v-model:visible="showReject"
	>
		<div class="flex flex-column gap-4">
			<p class="m-0 text-sm">
				Reject
				<strong>{{ rejectTarget?.variable_name }}</strong>
				for
				<strong>{{ rejectTarget?.period_name }}</strong>?
			</p>
			<div class="flex flex-column gap-2">
				<label class="text-primary-50" for="reject-notes">Reason (optional)</label>
				<PInputText id="reject-notes" size="small" v-model="decisionNotes" />
			</div>
			<div class="flex align-items-center justify-content-end gap-3">
				<PButton
					@click="doReject()"
					label="Reject"
					severity="danger"
					size="small"
				/>
				<PButton
					@click="showReject = false"
					label="Cancel"
					outlined
					severity="secondary"
					size="small"
				/>
			</div>
		</div>
	</PDialog>
</template>

<script lang="ts">
	import { defineComponent } from 'vue'
	import store from '@/store'
	import ManualEntriesBroker from '@/brokers/ManualEntriesBroker'
	import Utils from '@/utils/Utils'
	import Constants from '@/helpers/Constants'

	export default defineComponent({
		name: 'ManualEntriesView',

		data() {
			return {
				loading: true,
				list: [] as any[],
				catalog: [] as any[],
				periods: [] as any[],
				filterPeriodId: null as number | null,
				filterStatus: null as string | null,
				statusOptions: [
					{ label: 'Pending', value: 'pending' },
					{ label: 'Approved', value: 'approved' },
					{ label: 'Rejected', value: 'rejected' },
				],
				showSubmit: false,
				showApprove: false,
				approveTarget: null as any,
				showReject: false,
				rejectTarget: null as any,
				decisionNotes: '',
				form: {
					billing_period_id: null as number | null,
					variable_id: null as number | null,
					value: null as number | null,
					value_text: '',
					notes: '',
				},
			}
		},

		computed: {
			canCreate(): boolean {
				return store.getters.can('manual_entries', 'create')
			},
			canApprove(): boolean {
				return store.getters.can('manual_entries', 'approve')
			},
			periodOptions(): any[] {
				return this.periods.map((p: any) => ({
					label: `${p.name}${p.is_trial ? ' (trial)' : ''}`,
					value: p.id,
				}))
			},
			eligiblePeriodOptions(): any[] {
				return this.periods
					.filter((p: any) => p.status === 'open' || p.status === 'finalized')
					.map((p: any) => ({
						label: `${p.name} · ${p.status}${p.is_trial ? ' (trial)' : ''}`,
						value: p.id,
					}))
			},
		},

		async mounted() {
			Utils.generatePid(Constants.OPID.READ)
			await this.loadPeriods()
			await this.refreshList()
			this.loading = false
		},

		methods: {
			formatDate(value: string) {
				if (!value) return '—'
				const d = new Date(value)
				return isNaN(d.getTime()) ? value : d.toLocaleString()
			},

			displayValue(data: any) {
				if (data.value != null && data.value !== '') return data.value
				if (data.value_text) return data.value_text
				return '—'
			},

			statusSeverity(status: string) {
				if (status === 'approved') return 'success'
				if (status === 'rejected') return 'danger'
				return 'warning'
			},

			isOwnEntry(data: any) {
				const me = store.getters.getUsername
				return !!me && data.submitted_by_username === me
			},

			async loadPeriods() {
				try {
					this.periods = await ManualEntriesBroker.getBillingPeriods()
				} catch {
					this.periods = []
				}
			},

			async refreshList() {
				this.loading = true
				Utils.generatePid(Constants.OPID.READ)
				try {
					const res = await ManualEntriesBroker.list({
						billing_period_id: this.filterPeriodId,
						status: this.filterStatus,
					})
					this.list = res.entries
					this.catalog = res.catalog
				} catch {
					this.list = []
				}
				this.loading = false
			},

			openSubmit() {
				this.form = {
					billing_period_id: this.filterPeriodId,
					variable_id: this.catalog.length === 1 ? this.catalog[0].id : null,
					value: null,
					value_text: '',
					notes: '',
				}
				this.showSubmit = true
			},

			closeSubmit() {
				this.showSubmit = false
			},

			async doSubmit() {
				if (!this.form.billing_period_id) {
					Utils.showErrorMessage('Billing period is required.')
					return
				}
				if (!this.form.variable_id) {
					Utils.showErrorMessage('Variable is required.')
					return
				}
				const hasValue = this.form.value != null
				const hasText = !!(this.form.value_text && this.form.value_text.trim())
				if (!hasValue && !hasText) {
					Utils.showErrorMessage('Value or value text is required.')
					return
				}

				this.loading = true
				Utils.generatePid(Constants.OPID.ADD)
				try {
					const res = await ManualEntriesBroker.submit({
						variable_id: this.form.variable_id,
						billing_period_id: this.form.billing_period_id,
						value: this.form.value,
						value_text: hasText ? this.form.value_text.trim() : null,
						notes: this.form.notes?.trim() || null,
					})
					if (res) {
						this.closeSubmit()
						await this.refreshList()
						Utils.showSuccessMessage('Manual entry submitted for approval.')
					}
				} catch {
					// AxiosManager already toasts the server message
				}
				this.loading = false
			},

			openApprove(data: any) {
				this.approveTarget = data
				this.decisionNotes = ''
				this.showApprove = true
			},

			async doApprove() {
				if (!this.approveTarget) return
				this.loading = true
				Utils.generatePid(Constants.OPID.UPDATE)
				try {
					const res = await ManualEntriesBroker.approve(
						this.approveTarget.id,
						this.decisionNotes?.trim() || null,
					)
					if (res) {
						this.showApprove = false
						this.approveTarget = null
						await this.refreshList()
						Utils.showSuccessMessage('Manual entry approved and applied.')
					}
				} catch {
					// toasted
				}
				this.loading = false
			},

			confirmReject(_event: any, data: any) {
				this.rejectTarget = data
				this.decisionNotes = ''
				this.showReject = true
			},

			async doReject() {
				if (!this.rejectTarget) return
				this.loading = true
				Utils.generatePid(Constants.OPID.UPDATE)
				try {
					const res = await ManualEntriesBroker.reject(
						this.rejectTarget.id,
						this.decisionNotes?.trim() || null,
					)
					if (res) {
						this.showReject = false
						this.rejectTarget = null
						await this.refreshList()
						Utils.showSuccessMessage('Manual entry rejected.')
					}
				} catch {
					// toasted
				}
				this.loading = false
			},
		},
	})
</script>
